import React, { useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import { useContent } from '../../context/ContentContext';
import { promptImageUrl, readImageFile } from '../../utils/imageUpload';
import './editable.css';

function resolveImageList(getContent, contentKey, defaultImages) {
  const stored = getContent(contentKey, null);
  return Array.isArray(stored) ? stored : defaultImages;
}

export default function EditableImageGallery({
  contentKey,
  defaultImages,
  itemClassName = 'editable-gallery-item',
  imageClassName = 'editable-gallery-image',
  gridClassName = '',
  addLabel = 'Add image',
}) {
  const { isEditing } = useAuth();
  const { controlsVisible, activeFieldKey } = useEditMode();
  const showEditTools = isEditing && (controlsVisible || activeFieldKey === contentKey);
  const { getContent, setContentValue } = useContent();
  const addInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceIndexRef = useRef(null);

  const images = resolveImageList(getContent, contentKey, defaultImages);

  const saveImages = (nextImages) => {
    setContentValue(contentKey, nextImages);
  };

  const addImageSrc = (imageSrc) => {
    saveImages([...images, imageSrc]);
  };

  const replaceImageAt = (index, imageSrc) => {
    const nextImages = [...images];
    nextImages[index] = imageSrc;
    saveImages(nextImages);
  };

  const removeImageAt = (index) => {
    if (!window.confirm('Remove this image from the gallery?')) return;
    saveImages(images.filter((_, itemIndex) => itemIndex !== index));
  };

  const applyFile = async (file, handler) => {
    try {
      const dataUrl = await readImageFile(file);
      handler(dataUrl);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const openReplacePicker = (index) => {
    replaceIndexRef.current = index;
    replaceInputRef.current?.click();
  };

  const handleAddUrl = () => {
    const url = promptImageUrl();
    if (url) addImageSrc(url);
  };

  return (
    <div className={gridClassName || undefined} data-content-key={contentKey}>
      {images.map((image, index) => (
        <div key={`${contentKey}-${index}`} className={itemClassName}>
          <img src={image} alt={`Gallery ${index + 1}`} className={imageClassName} />
          {showEditTools && (
            <span className="editable-image-toolbar editable-image-toolbar--gallery" role="toolbar" aria-label={`Edit image ${index + 1}`}>
              <button type="button" className="editable-image-btn" onClick={() => openReplacePicker(index)}>
                Replace
              </button>
              <button
                type="button"
                className="editable-image-btn"
                onClick={() => {
                  const url = promptImageUrl();
                  if (url) replaceImageAt(index, url);
                }}
              >
                URL
              </button>
              <button
                type="button"
                className="editable-image-btn editable-image-btn--danger"
                onClick={() => removeImageAt(index)}
              >
                Remove
              </button>
            </span>
          )}
        </div>
      ))}

      {showEditTools && (
        <div className={`${itemClassName} editable-gallery-add`}>
          <button type="button" className="editable-gallery-add-btn" onClick={() => addInputRef.current?.click()}>
            + {addLabel}
          </button>
          <button type="button" className="editable-gallery-add-btn editable-gallery-add-btn--secondary" onClick={handleAddUrl}>
            Add from URL
          </button>
        </div>
      )}

      <input
        ref={addInputRef}
        type="file"
        accept="image/*"
        className="editable-image-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) applyFile(file, addImageSrc);
          e.target.value = '';
        }}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        className="editable-image-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          const index = replaceIndexRef.current;
          if (file && index !== null) applyFile(file, (src) => replaceImageAt(index, src));
          e.target.value = '';
          replaceIndexRef.current = null;
        }}
      />
    </div>
  );
}
